import { styled } from '@linaria/react';

import { colors, fonts, layout, semantic, space } from '../../theme/tokens';

export const PageWrapper = styled.section`
  width: 100%;
  max-width: ${layout.narrowMaxWidth};
  margin: 0 auto;
  padding: ${space[4]} ${space[4]} ${space[8]};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${space[5]};

  @media (min-width: ${layout.desktopBreakpoint}) {
    padding: ${space[6]} ${space[5]} ${space[10]};
    gap: ${space[6]};
  }
`;

export const BackRow = styled.div`
  display: flex;
`;

export const BackChip = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: ${layout.borderRadiusFull};
  border: 1px solid ${semantic.border.default};
  background: ${colors.white};
  color: ${semantic.text.primary};
  cursor: pointer;
  padding: 0;
  transition: background 0.15s ease;

  &:hover {
    background: ${semantic.bg.surfaceSubtle};
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`;

export const HeaderCard = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${space[4]};
  padding: ${space[5]};
  background: ${colors.white};
  border: 1px solid ${semantic.border.subtle};
  border-radius: ${layout.borderRadiusLg};

  @media (min-width: ${layout.desktopBreakpoint}) {
    padding: ${space[6]};
  }
`;

export const Title = styled.h1`
  margin: 0;
  font-family: ${fonts.heading};
  font-size: ${fonts.size.xl};
  font-weight: ${fonts.weight.semiBold};
  color: ${semantic.text.primary};

  @media (min-width: ${layout.desktopBreakpoint}) {
    font-size: ${fonts.size['2xl']};
  }
`;

export const StatusPill = styled.span`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  padding: ${space[1]} ${space[3]};
  border-radius: ${layout.borderRadiusFull};
  font-family: ${fonts.body};
  font-size: ${fonts.size.xs};
  font-weight: ${fonts.weight.medium};

  &[data-status='active'] {
    background: ${semantic.bg.positiveSubtle};
    color: ${semantic.text.positive};
  }

  &[data-status='cancelled'] {
    background: ${semantic.bg.dangerSubtle};
    color: ${semantic.text.danger};
  }

  &[data-status='complete'] {
    background: ${semantic.bg.surfaceSubtle};
    color: ${semantic.text.primary};
  }
`;

export const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${space[4]};
  align-items: start;

  @media (min-width: ${layout.desktopBreakpoint}) {
    grid-template-columns: repeat(4, minmax(0, 1fr)) auto;
    gap: ${space[5]};
  }
`;

export const SummaryField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const SummaryLabel = styled.span`
  font-family: ${fonts.body};
  font-size: ${fonts.size.xs};
  color: ${semantic.text.secondary};
`;

export const SummaryValue = styled.span`
  font-family: ${fonts.body};
  font-size: ${fonts.size.sm};
  font-weight: ${fonts.weight.medium};
  color: ${semantic.text.primary};
  white-space: pre-line;
`;

export const HeaderActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[2]};

  & > button {
    width: 100%;
  }

  @media (min-width: ${layout.desktopBreakpoint}) {
    flex-direction: row;
    align-self: end;
    justify-self: end;

    & > button {
      width: auto;
    }
  }
`;

export const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[4]};
`;

export const ItemCard = styled.article`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${space[4]};
  padding: ${space[4]};
  background: ${colors.white};
  border: 1px solid ${semantic.border.subtle};
  border-radius: ${layout.borderRadiusLg};

  @media (min-width: ${layout.desktopBreakpoint}) {
    grid-template-columns: 240px 1fr;
    gap: ${space[5]};
    padding: ${space[5]};
  }

  &[data-selectable='false'] {
    opacity: 0.6;
  }
`;

export const ItemPolicyHint = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['2px']};
  font-family: ${fonts.body};
  font-size: ${fonts.size.xs};
  color: ${semantic.text.secondary};
`;

export const ItemSelectionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: ${space[2]} ${space[3]};
  background: ${semantic.bg.surfaceSubtle};
  border-radius: ${layout.borderRadiusMd};
  font-family: ${fonts.body};
  font-size: ${fonts.size.sm};
  font-weight: ${fonts.weight.semiBold};
  color: ${semantic.text.primary};
`;

export const ItemImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: ${layout.borderRadiusMd};
  background: ${semantic.bg.surfaceSubtle};
`;

export const ItemImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const ItemImagePlaceholder = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${semantic.text.secondary};
  font-family: ${fonts.body};
  font-size: ${fonts.size.sm};
`;

export const ItemBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};
  min-width: 0;
`;

export const ItemHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${space[3]};
`;

export const ItemTitle = styled.h2`
  margin: 0;
  font-family: ${fonts.heading};
  font-size: ${fonts.size.lg};
  font-weight: ${fonts.weight.semiBold};
  color: ${semantic.text.primary};
`;

export const ItemPriceColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  white-space: nowrap;
`;

export const ItemPrice = styled.span`
  font-family: ${fonts.body};
  font-size: ${fonts.size.lg};
  font-weight: ${fonts.weight.semiBold};
  color: ${semantic.text.primary};
`;

export const ItemSubPrice = styled.span`
  font-family: ${fonts.body};
  font-size: ${fonts.size.xs};
  color: ${semantic.text.secondary};
`;

export const ItemDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${space[3]} ${space[4]};
`;

export const DetailField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const DetailLabel = styled.span`
  font-family: ${fonts.body};
  font-size: ${fonts.size.xs};
  color: ${semantic.text.secondary};
`;

export const DetailValue = styled.span`
  font-family: ${fonts.body};
  font-size: ${fonts.size.sm};
  font-weight: ${fonts.weight.medium};
  color: ${semantic.text.primary};
  overflow-wrap: anywhere;
`;

export const Footer = styled.div`
  display: flex;
  justify-content: center;

  & > button {
    width: 100%;

    @media (min-width: ${layout.desktopBreakpoint}) {
      width: 480px;
      max-width: 100%;
    }
  }
`;
